import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import { AppDispatch, RootState } from "../../../../redux/store";
import { CheckCircle, Minus, Plus, WifiHigh } from "phosphor-react";

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const WifiDetails: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [wifi,          setWifi]          = useState({ name: "", password: "" });
  const [internetSpeed, setInternetSpeed] = useState<number>(0);
  const [hasChanges,    setHasChanges]    = useState(false);
  const [isSaving,      setIsSaving]      = useState(false);
  const [saved,         setSaved]         = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (currentListing) {
      setWifi(prev => {
        const n = { name: currentListing.wifi?.name || "", password: currentListing.wifi?.password || "" };
        return prev.name !== n.name || prev.password !== n.password ? n : prev;
      });
      setInternetSpeed(prev => {
        const s = currentListing.internetSpeed || 0;
        return prev !== s ? s : prev;
      });
    }
  }, [currentListing]);

  const handleChange = (key: "name" | "password", value: string) => {
    setWifi(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const adjustSpeed = (step: number) => {
    setInternetSpeed(prev => Math.max(0, prev + step));
    setHasChanges(true);
  };

  const isReadyToSave = wifi.name.trim() !== "" && wifi.password.trim() !== "" && hasChanges && !isSaving;

  const handleSave = async () => {
    if (!listingId || !isReadyToSave) return;
    setIsSaving(true);
    try {
      await dispatch(upsertShortstay({ listingId, data: { wifi, internetSpeed } }));
      setHasChanges(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // handle error silently or add error toast
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: "#FAFAFA",
    color: T.black,
    fontFamily: T.sans,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color .15s",
  };

  return (
    <div style={{ fontFamily: T.sans, color: T.text, maxWidth: 500, margin: "0 auto", position: "relative" }}>

      {/* ── SAVING OVERLAY ── */}
      {isSaving && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(255,255,255,0.65)",
          backdropFilter: "blur(6px)",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
        }}>
          <div style={{
            width: 56, height: 56,
            borderRadius: "50%",
            border: `3px solid ${T.border}`,
            borderTop: `3px solid ${T.black}`,
            animation: "spin 0.8s linear infinite",
          }} />
          <p style={{
            fontSize: 12, fontWeight: 500,
            color: T.black, letterSpacing: "0.08em",
            textTransform: "uppercase", margin: 0,
          }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── SUCCESS TOAST ── */}
      {saved && (
        <div style={{
          position: "fixed",
          bottom: 32, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          background: "#FFFFFF",
          border: "1px solid #D4EDDA",
          borderRadius: 12,
          padding: "14px 22px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          minWidth: 220,
        }}>
          <CheckCircle size={20} weight="fill" color="#22883F" />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>
              Successfully saved
            </p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>
              Wi-Fi details updated
            </p>
          </div>
        </div>
      )}

      {/* heading */}
      <div style={{ marginBottom: 32 }}>
        <p style={{
          fontSize: 9, color: T.muted, letterSpacing: "0.13em",
          textTransform: "uppercase", margin: "0 0 6px",
        }}>
          Guest experience
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>
          Wi-Fi details
        </h2>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
          Guests will see this in their check-in instructions.
        </p>
      </div>

      {/* form card */}
      <div style={{
        background: "#FFFFFF",
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: "24px 20px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}>

        {/* icon header */}
        <div style={{
          width: 48, height: 48,
          borderRadius: "50%",
          background: "#F5F5F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.black,
        }}>
          <WifiHigh size={24} />
        </div>

        {/* Wi-Fi Name */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: T.textSoft, letterSpacing: "0.04em" }}>
            Network name
          </label>
          <input
            type="text"
            placeholder="e.g. Home_Network"
            value={wifi.name}
            onChange={e => handleChange("name", e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = T.black)}
            onBlur={e => (e.target.style.borderColor = T.border)}
          />
        </div>

        {/* Wi-Fi Password */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: T.textSoft, letterSpacing: "0.04em" }}>
            Password
          </label>
          <input
            type="text"
            placeholder="e.g. mysecurepass123"
            value={wifi.password}
            onChange={e => handleChange("password", e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = T.black)}
            onBlur={e => (e.target.style.borderColor = T.border)}
          />
        </div>

        {/* Internet Speed */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: T.textSoft, letterSpacing: "0.04em" }}>
            Internet speed
          </label>

          {/* stepper */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            overflow: "hidden",
            background: "#FAFAFA",
          }}>
            <button
              onClick={() => adjustSpeed(-5)}
              style={{
                width: 44, height: 44,
                border: "none",
                borderRight: `1px solid ${T.border}`,
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.black,
                transition: "background .15s",
                flexShrink: 0,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "#F0F0F0")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <Minus size={16} />
            </button>

            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}>
              <input
                type="number"
                value={internetSpeed}
                onChange={e => {
                  setInternetSpeed(Math.max(0, Number(e.target.value)));
                  setHasChanges(true);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  fontFamily: T.sans,
                  fontSize: 15,
                  fontWeight: 600,
                  color: T.black,
                  width: 60,
                  textAlign: "center",
                  outline: "none",
                  padding: 0,
                }}
              />
              <span style={{ fontSize: 12, color: T.muted, fontWeight: 400 }}>Mbps</span>
            </div>

            <button
              onClick={() => adjustSpeed(5)}
              style={{
                width: 44, height: 44,
                border: "none",
                borderLeft: `1px solid ${T.border}`,
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.black,
                transition: "background .15s",
                flexShrink: 0,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "#F0F0F0")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* save */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <button
          onClick={handleSave}
          disabled={!isReadyToSave}
          style={{
            background: isReadyToSave ? T.black : "#F0F0F0",
            color: isReadyToSave ? "#FFFFFF" : T.muted,
            border: "none",
            borderRadius: 10,
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            cursor: isReadyToSave ? "pointer" : "not-allowed",
            letterSpacing: "0.04em",
            transition: "all .2s ease",
            boxShadow: isReadyToSave ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
          }}
        >
          Save changes
        </button>
      </div>
    </div>
  );
};

export default WifiDetails;